/**
 * Rebuild an assistant message's cards from what do-chat-api stored on it.
 *
 * Every card here is first drawn from a `tool_result` SSE event, which is long
 * gone by the time the thread is fetched again. The API persists each one onto
 * the message for exactly that reason, so anything it stores and this file does
 * not read is a card that survives until the next reload and then vanishes.
 */

function cards(metadata, key, type) {
  if (!Array.isArray(metadata?.[key])) return []
  return metadata[key].map((card) => ({ type, ...card }))
}

// Stored as one object, not a list — a turn asks once. The message id is not on
// the stored card because the card is *on* that message; the browser needs it to
// submit, so it is stamped back on here.
function clarification(metadata, assistantMessageId) {
  const card = metadata?.clarification
  if (!card || typeof card !== 'object' || !assistantMessageId) return []
  return [{ type: 'clarification_card', ...card, assistant_message_id: assistantMessageId }]
}

function responsePart(part, assistantMessageId) {
  if (!part || typeof part !== 'object') return null
  if (part.type === 'markdown' && typeof part.text === 'string') return part
  if ((part.type === 'integration_access_request' || part.kind === 'integration_access_request') && part.review_id) {
    return { ...part, type: 'integration_access_request', assistant_message_id: assistantMessageId }
  }
  if (!part.block || typeof part.block !== 'object' || !part.type) return null
  const block = { type: part.type, ...part.block }
  if (block.type === 'clarification_card' && assistantMessageId) {
    block.assistant_message_id = assistantMessageId
  }
  return block
}

export function messageResponseParts(metadata, assistantMessageId) {
  if (!Array.isArray(metadata?.response_parts_v1)) return []
  return metadata.response_parts_v1
    .map((part) => responsePart(part, assistantMessageId))
    .filter(Boolean)
}

export function messageCardBlocks(metadata, assistantMessageId) {
  const accessReviews = cards(metadata, 'integration_access_reviews', 'integration_access_request')
    .map((card) => ({ ...card, assistant_message_id: assistantMessageId }))
  return [
    ...cards(metadata, 'artifacts', 'artifact_card'),
    // Without this a pending approval vanishes on reload: the write is still
    // waiting server-side, but the only thing that could authorise it is gone.
    // The card resolves its own current status on mount, so a stale "pending"
    // stored here never shows an Approve button for something already sent.
    ...cards(metadata, 'proposals', 'confirmation_card'),
    // A drafted schedule is not armed, so a card lost on reload is a schedule
    // the user believes exists and which never fires. `useScheduleStart` re-reads
    // the row on mount, so one already started comes back saying so rather than
    // offering Start a second time.
    ...cards(metadata, 'workflow_drafts', 'automation_card'),
    ...cards(metadata, 'automation_drafts', 'automation_setup_card'),
    // Same reasoning as proposals: the connection is still broken after a
    // reload, so the offer to fix it has to survive one.
    ...cards(metadata, 'connect_requests', 'connect_card'),
    ...accessReviews,
    // Last, like the SSE emission order: the question the turn ended on belongs
    // below whatever it produced before asking.
    ...clarification(metadata, assistantMessageId),
  ]
}
